import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, ListGroup, ProgressBar } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAuth } from '../contexts/AuthContext';

export default function GameResults() {
  const {   gameSession, leaveGame, isInGame, getPublicGames } = useGame();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showStats, setShowStats] = useState(false);
  
  // Get game data from gameSession.game if available, otherwise fall back to currentGame
  const gameData = gameSession ;

  useEffect(() => {
    if (!isInGame || !gameData || !gameSession) {
      navigate('/');
      return;
    }
  }, [isInGame, gameData, gameSession, navigate]);

  useEffect(() => {
    // Show stats after a brief delay for dramatic effect
    const timer = setTimeout(() => setShowStats(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handlePlayAgain = async () => {
    await leaveGame();
    await getPublicGames();
    navigate('/');
  };

  const currentPlayer = gameSession?.players.find(p => p.id === user?.id);
  const finalRank = gameSession?.leaderboard?.findIndex(entry => entry.playerId === user?.id) + 1 || 0;
  const totalPlayers = gameSession?.leaderboard?.length || 0;

  const getRankBadge = (rank: number) => {
    switch (rank) {
      case 1: return { bg: 'warning', icon: 'fas fa-crown', text: '1st Place!' };
      case 2: return { bg: 'secondary', icon: 'fas fa-medal', text: '2nd Place' };
      case 3: return { bg: 'warning', icon: 'fas fa-medal', text: '3rd Place' };
      default: return { bg: 'primary', icon: 'fas fa-user', text: `${rank}${getOrdinalSuffix(rank)} Place` };
    }
  };

  const getOrdinalSuffix = (num: number) => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  const getPerformanceMessage = (rank: number, totalPlayers: number, accuracy: number) => {
    const percentage = ((totalPlayers - rank + 1) / totalPlayers) * 100;
    
    if (rank === 1) return "🎉 Fantastic! You're the trivia champion!";
    if (percentage >= 75) return "🌟 Great job! You really know your stuff!";
    if (percentage >= 50) return "👍 Well done! Solid performance!";
    if (percentage >= 25) return "📚 Not bad! Keep studying and you'll improve!";
    return "💪 Keep practicing - you'll get better!";
  };

  if (!gameData || !gameSession) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <div className="spinner-border text-primary" />
            <p className="mt-2 text-muted">Loading results...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  const rankInfo = getRankBadge(finalRank);
  const accuracy = currentPlayer && currentPlayer.totalAnswers > 0 
    ? (currentPlayer.correctAnswers / currentPlayer.totalAnswers) * 100 
    : 0;

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={10}>
          {/* Game Completed Header */}
          <div className="text-center mb-4">
            <h1 className="display-4 text-primary mb-2">Game Complete!</h1>
            <p className="lead text-muted">{gameData.title}</p>
          </div>

          {/* Player Results */}
          {currentPlayer && showStats && (
            <Card className="border-0 shadow-lg mb-4">
              <Card.Body className="p-4 text-center">
                <div className="mb-4">
                  <Badge bg={rankInfo.bg} className="px-4 py-3 fs-5 mb-3">
                    <i className={`${rankInfo.icon} me-2`}></i>
                    {rankInfo.text}
                  </Badge>
                  <p className="lead mb-0">
                    {getPerformanceMessage(finalRank, totalPlayers, accuracy)}
                  </p>
                </div>

                <Row className="g-4">
                  <Col md={3} className="text-center">
                    <div className="h2 text-primary mb-1">{currentPlayer.score}</div>
                    <div className="text-muted">Final Score</div>
                  </Col>
                  <Col md={3} className="text-center">
                    <div className="h2 text-success mb-1">{currentPlayer.correctAnswers}</div>
                    <div className="text-muted">Correct Answers</div>
                  </Col>
                  <Col md={3} className="text-center">
                    <div className="h2 text-info mb-1">{Math.round(accuracy)}%</div>
                    <div className="text-muted">Accuracy</div>
                  </Col>
                  <Col md={3} className="text-center">
                    <div className="h2 text-warning mb-1">{finalRank}/{totalPlayers}</div>
                    <div className="text-muted">Final Rank</div>
                  </Col>
                </Row>

                {/* Progress Bar for Accuracy */}
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold">Accuracy Rate</span>
                    <span className="text-muted">{Math.round(accuracy)}%</span>
                  </div>
                  <ProgressBar 
                    now={accuracy} 
                    variant={accuracy >= 80 ? 'success' : accuracy >= 60 ? 'warning' : 'danger'}
                    style={{ height: '12px' }}
                    animated
                  />
                </div>
              </Card.Body>
            </Card>
          )}

          <Row>
            <Col lg={8}>
              {/* Final Leaderboard */}
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-trophy me-2"></i>
                    Final Leaderboard
                  </h5>
                  <Badge bg="light" text="dark">
                    {totalPlayers} Players
                  </Badge>
                </Card.Header>
                <Card.Body className="p-0">
                  <ListGroup variant="flush">
                    {gameSession.leaderboard.map((entry, index) => (
                      <ListGroup.Item 
                        key={entry.playerId}
                        className={`d-flex justify-content-between align-items-center ${
                          entry.playerId === user?.id ? 'bg-light border-primary border-2' : ''
                        }`}
                      >
                        <div className="d-flex align-items-center">
                          <div className="me-3">
                            {index < 3 ? (
                              <Badge 
                                bg={index === 0 ? 'warning' : 'secondary'} 
                                className="px-3 py-2"
                              >
                                <i className={index === 0 ? 'fas fa-crown' : 'fas fa-medal'}></i>
                              </Badge>
                            ) : (
                              <Badge bg="outline-primary" className="px-3 py-2">
                                {index + 1}
                              </Badge>
                            )}
                          </div>
                          <div>
                            <div className={`fw-semibold ${entry.playerId === user?.id ? 'text-primary' : ''}`}>
                              {entry.playerName}
                              {entry.playerId === user?.id && (
                                <small className="text-primary ms-1">(You)</small>
                              )}
                            </div>
                            <small className="text-muted">
                              {entry.correctAnswers}/{entry.totalAnswers} correct • {entry.accuracyRate}% accuracy
                            </small>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="h5 mb-0 text-primary">{entry.score}</div>
                          <small className="text-muted">points</small>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              {/* Game Stats */}
              <Card className="border-0 shadow-sm mb-3">
                <Card.Header className="bg-info text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-chart-bar me-2"></i>
                    Game Statistics
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="row g-3">
                    <div className="col-6 text-center">
                      <div className="h5 text-primary mb-0">{gameSession.totalQuestionCount}</div>
                      <small className="text-muted">Questions</small>
                    </div>
                    <div className="col-6 text-center">
                      <div className="h5 text-success mb-0">{totalPlayers}</div>
                      <small className="text-muted">Players</small>
                    </div>
                    <div className="col-6 text-center">
                      <div className="h5 text-warning mb-0">
                        {Math.round(gameSession.leaderboard.reduce((sum, p) => sum + p.score, 0) / totalPlayers)}
                      </div>
                      <small className="text-muted">Avg Score</small>
                    </div>
                    <div className="col-6 text-center">
                      <div className="h5 text-info mb-0">
                        {Math.round(gameSession.leaderboard.reduce((sum, p) => sum + p.accuracyRate, 0) / totalPlayers)}%
                      </div>
                      <small className="text-muted">Avg Accuracy</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Actions */}
              <Card className="border-0 shadow-sm">
                <Card.Body className="d-grid gap-2">
                  <Button 
                    variant="primary" 
                    size="lg"
                    onClick={handlePlayAgain}
                  >
                    <i className="fas fa-redo me-2"></i>
                    Play Again
                  </Button>
                  <Button 
                    as={Link} 
                    to="/" 
                    variant="outline-primary"
                  >
                    <i className="fas fa-home me-2"></i>
                    Back to Home
                  </Button>
                  {user && !user.isGuest && (
                    <Button 
                      as={Link} 
                      to="/profile" 
                      variant="outline-secondary"
                    >
                      <i className="fas fa-user me-2"></i>
                      View Profile
                    </Button>
                  )}
                </Card.Body>
              </Card>

              {/* Share Results */}
              <Card className="border-0 bg-light mt-3">
                <Card.Body className="text-center">
                  <h6>Share Your Results</h6>
                  <p className="small text-muted mb-3">
                    I scored {currentPlayer?.score} points in {gameData.title} on TriviYolo!
                  </p>
                  <div className="d-flex gap-2 justify-content-center">
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => {
                        const text = `I just scored ${currentPlayer?.score} points in "${gameData.title}" on TriviYolo! 🎉`;
                        if (navigator.share) {
                          navigator.share({ text });
                        } else {
                          navigator.clipboard.writeText(text);
                        }
                      }}
                    >
                      <i className="fas fa-share me-1"></i>
                      Share
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
}
